/**
 * @file blockedCountries.ts
 * @brief Pure helpers for Super Admin → Billing → Blocked countries
 *
 * @details
 * DES 22-10-2026: every country can buy plans except a Super Admin
 * block-list (BACKEND B1, `SUBSCRIPTION_BLOCKED_COUNTRIES`; no row = EU 27 +
 * EEA + sanctions). The page edits the list locally, shows "Will change:
 * + AF, − FR" against what it loaded, then PUTs the whole list.
 * Related: BlockedCountriesSection.tsx, BACKEND blockedCountries.test.mjs
 *
 * @author DES
 * @date 22-10-2026
 */

/** Trim, upper-case and dedupe, keeping the first-seen order. */
export function normalizeCodes(codes: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of codes) {
    const code = raw.trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

export interface BlockedDiff {
  /** Blocked now, not before (sorted). */
  added: string[];
  /** Blocked before, not now (sorted). */
  removed: string[];
}

export function diffBlocked(
  before: readonly string[],
  after: readonly string[],
): BlockedDiff {
  const b = new Set(normalizeCodes(before));
  const a = new Set(normalizeCodes(after));
  return {
    added: [...a].filter((c) => !b.has(c)).sort(),
    removed: [...b].filter((c) => !a.has(c)).sort(),
  };
}

export function hasChanges(diff: BlockedDiff): boolean {
  return diff.added.length > 0 || diff.removed.length > 0;
}

/** "+ AF, − FR, − DE" (added first). Empty string when nothing changes. */
export function changeLine(diff: BlockedDiff): string {
  return [
    ...diff.added.map((c) => `+ ${c}`),
    ...diff.removed.map((c) => `− ${c}`),
  ].join(", ");
}

/** "All other countries are open (36 blocked)." */
export function openLine(blockedCount: number): string {
  return blockedCount === 0
    ? "Every country is open (none blocked)."
    : `All other countries are open (${blockedCount} blocked).`;
}
